import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import BadgeIcon from './BadgeIcon';
import { BadgeType, BadgeTier, BADGE_REQUIREMENTS, getBadgeTier, getTierLevel } from '../utils/badges';
import { trackBadgesPopup } from '../utils/analytics';
import { getScore, getTotalStars } from '../utils/completionStorage';
import { sound } from '../utils/sound';

interface BadgeInfoPopupProps {
  onClose: () => void;
}

const BadgeInfoPopup: React.FC<BadgeInfoPopupProps> = ({ onClose }) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    sound.clickClose();
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200); // Match animation duration
  };

  // Get current tier for each badge type
  const streakTier = getBadgeTier(BadgeType.STREAK);
  const geniusTier = getBadgeTier(BadgeType.GENIUS);
  const completionTier = getBadgeTier(BadgeType.COMPLETION);

  // Track badges popup on mount
  useEffect(() => {
    // Calculate num_total_badges (cumulative: bronze=1, silver=2, gold=3 per badge type)
    const numTotalBadges = getTierLevel(streakTier) + getTierLevel(geniusTier) + getTierLevel(completionTier);

    trackBadgesPopup({
      total_score: getScore(),
      total_stars: getTotalStars(),
      num_total_badges: numTotalBadges
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const getTitle = (type: BadgeType): string => {
    switch (type) {
      case BadgeType.STREAK:
        return "Streak";
      case BadgeType.GENIUS:
        return "Genius";
      case BadgeType.COMPLETION:
        return "Completion";
      default:
        return "";
    }
  };

  const getDescription = (type: BadgeType): string => {
    switch (type) {
      case BadgeType.STREAK:
        return "Consecutive days completed";
      case BadgeType.GENIUS:
        return "Days with 3 stars";
      case BadgeType.COMPLETION:
        return "Month completed percentage";
      default:
        return "";
    }
  };

  // Check if a tier is earned for a given badge type
  const isTierEarned = (type: BadgeType, tier: BadgeTier): boolean => {
    const currentTier = type === BadgeType.STREAK ? streakTier :
                        type === BadgeType.GENIUS ? geniusTier :
                        completionTier;

    if (currentTier === BadgeTier.NONE) return false;
    if (tier === BadgeTier.BRONZE) return true; // If any tier earned, bronze is earned
    if (tier === BadgeTier.SILVER) return currentTier === BadgeTier.SILVER || currentTier === BadgeTier.GOLD;
    if (tier === BadgeTier.GOLD) return currentTier === BadgeTier.GOLD;
    return false;
  };

  const getRequirement = (type: BadgeType, tier: BadgeTier): string => {
    const reqs = BADGE_REQUIREMENTS[type];
    const value = tier === BadgeTier.BRONZE ? reqs.bronze :
                  tier === BadgeTier.SILVER ? reqs.silver :
                  reqs.gold;

    if (type === BadgeType.COMPLETION) {
      return `${Math.round(value * 100)}%`;
    }
    return `${value} day${value !== 1 ? 's' : ''}`;
  };

  const badgeTypes = [BadgeType.STREAK, BadgeType.COMPLETION, BadgeType.GENIUS];
  const tiers = [BadgeTier.BRONZE, BadgeTier.SILVER, BadgeTier.GOLD];
  const tierLabels = ['Bronze', 'Silver', 'Gold'];
  const tierClasses = ['bronze', 'silver', 'gold'];

  return createPortal(
    <div className={`badge-info-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`badge-info-content ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="badge-info-header">
          <h2>Badges</h2>
          <button className="badge-info-close-button" onClick={handleClose}>✕</button>
        </div>
        <div className="badge-info-body">
          <div className="badge-header-row">
            {badgeTypes.map((type) => (
              <div key={type} className="badge-column-header">
                <h3 className="badge-column-title">{getTitle(type)}</h3>
                <p className="badge-column-description">{getDescription(type)}</p>
              </div>
            ))}
          </div>
          {tiers.map((tier, tierIndex) => (
            <div key={tier} className="badge-tier-row">
              {badgeTypes.map((type) => (
                <div key={type} className="badge-tier-slot">
                  <div>
                    <div className="badge-icon-wrapper">
                      <BadgeIcon
                        type={type}
                        tier={isTierEarned(type, tier) ? tier : BadgeTier.NONE}
                        size="medium"
                      />
                      <span className="badge-requirement">{getRequirement(type, tier)}</span>
                    </div>
                    <span className={`tier-label ${tierClasses[tierIndex]}`}>{tierLabels[tierIndex]}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BadgeInfoPopup;
